import { Box, InputLabel, styled, type InputLabelProps } from '@mui/material';
import { useParams } from 'react-router-dom';
import { Show } from '../../../components/Show';
import { FieldExtractionPopover } from '../../../console/submissions/modules/_components/extraction/FieldExtractionPopover';
import { useFieldExtractionById } from '../../../console/submissions/modules/_components/extraction/useFieldExtractionById';
import { FieldType } from '../../parsing/parser';
import type { FormFieldDef } from '../../types';
import { FieldConfidenceChip } from './FieldConfidenceChip';

// Field types that support RAG extraction
const RAG_SUPPORTED_FIELD_TYPES: FieldType[] = [
  // Simple types (Phase 1)
  FieldType.Text,
  FieldType.Currency,
  FieldType.Number,
  FieldType.Radio,
  FieldType.Checkbox,
  FieldType.SingleSelect,
  FieldType.Dropdown,
  FieldType.Date,
  // Complex types (Phase 2)
  FieldType.CheckboxGrouping,
  FieldType.MultiSelect,
  FieldType.ItemizedPercentileList,
  FieldType.GroupedPercentileList,
  FieldType.JobList,
  FieldType.FieldList,
];

type FieldControlLabelProps = {
  hasLabel: boolean;
  isRequired: boolean;
  text?: string;
  confidenceScore?: number;
  showConfidence?: boolean;
  fieldId: string;
  formId: string;
  // RAG props
  enableRag?: boolean;
  submissionId?: string;
  fieldType?: FieldType;
  fieldMeta?: FormFieldDef;
} & InputLabelProps;

export const FieldControlLabel = ({
  hasLabel,
  isRequired,
  text,
  confidenceScore,
  showConfidence = false,
  fieldId,
  formId,
  enableRag = false,
  submissionId,
  fieldType,
  fieldMeta,
}: FieldControlLabelProps) => {
  const params = useParams<{ submissionId?: string }>();

  // Use submissionId from props or route params
  const effectiveSubmissionId = submissionId ?? params.submissionId ?? '';

  // Check if this field type supports RAG extraction (strict booleans)
  const isRagSupported = Boolean(
    fieldType && RAG_SUPPORTED_FIELD_TYPES.includes(fieldType)
  );
  const shouldEnableRag = Boolean(
    enableRag && isRagSupported && effectiveSubmissionId
  );

  // RAG extraction hook - only active when enabled and supported
  const ragProps = useFieldExtractionById(
    fieldId,
    text ?? '',
    effectiveSubmissionId,
    undefined, // conversationId
    shouldEnableRag // only enable hook when RAG is supported
  );

  const labelEl = hasLabel ? (
    <Box display="flex" alignItems="center" sx={{ flex: 1, pr: 4, gap: 1 }}>
      <StyledInputLabel required={isRequired}>{text}</StyledInputLabel>
      <Show when={showConfidence}>
        <FieldConfidenceChip
          fieldId={fieldId}
          formId={formId}
          confidenceScore={confidenceScore}
          submissionId={effectiveSubmissionId}
        />
      </Show>
      {/* RAG Icon - only for supported field types */}
      <Show when={shouldEnableRag && ragProps.show}>
        <FieldExtractionPopover
          {...ragProps}
          extraction={ragProps.extraction ?? null}
          fieldName={fieldId}
          fieldLabel={text}
          fieldType={fieldType}
          fieldMeta={fieldMeta}
        />
      </Show>
    </Box>
  ) : null;

  return labelEl;
};

export const StyledInputLabel = styled(InputLabel)(
  ({ theme: { palette } }) => ({
    '&.MuiInputLabel-root': {
      fontSize: '1.1rem',
      overflow: 'unset',
      whiteSpace: 'pre-line',
      wordBreak: 'break-word',
    },

    '.MuiInputLabel-asterisk': {
      color: palette.error.dark,
    },
  })
);
